/**
 * Knowledge · everything inside one category (/knowledge/categories/:templateId).
 * The middle level of Category → Subject → Guide: the category names what
 * these things ARE, so its own subject noun titles the list.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  IconButton,
  Input,
  Modal,
  Select,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { Trash2 } from '@servicecompressionllc/sc-design-system/icons';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { knowledgeApi, type KnowledgeSubject } from './api.js';
import { templateReadiness } from './readiness.js';
import { NewSubjectModal } from './NewSubjectModal.js';
import { CategoryOverviewTab, CategoryPipelineTab } from './CategorySettingsTabs.js';

export const CategorySubjectsPage = () => {
  const { templateId = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const templates = useQuery({
    queryKey: ['knowledge', 'templates'],
    queryFn: knowledgeApi.templates,
  });
  const subjects = useQuery({
    queryKey: ['knowledge', 'subjects'],
    queryFn: knowledgeApi.subjects,
  });
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeSubject | null>(null);
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => knowledgeApi.deleteSubject(id),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });
  const [tab, setTab] = useState<'subjects' | 'overview' | 'pipeline'>('subjects');

  // Filters live in the URL — a filtered list is a link someone can send.
  const [params, setParams] = useSearchParams();
  const kindFilter = params.get('type') ?? 'all';
  const search = params.get('q') ?? '';
  const setParam = (key: string, value: string, defaultValue: string) => {
    setParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  if (templates.isLoading || subjects.isLoading) return <Skeleton className="h-40" />;
  const template = (templates.data ?? []).find((t) => t.id === templateId);
  if (!template) return <div className="text-sm text-danger">Couldn’t find this category.</div>;

  const all = (subjects.data ?? []).filter((s) => !s.archived && s.templateId === templateId);
  const kinds = [...new Set(all.map((s) => s.kind).filter((k): k is string => k !== null))].sort();
  const q = search.trim().toLowerCase();
  const readiness = templateReadiness(template);
  const rows = all.filter(
    (s) =>
      (kindFilter === 'all' || s.kind === kindFilter) &&
      (q.length === 0 ||
        s.name.toLowerCase().includes(q) ||
        s.aliases.some((a) => a.toLowerCase().includes(q))),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <section className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Breadcrumbs trail={[{ label: 'Categories', to: '/knowledge/categories' }]} />
          <h2 className="text-2xl font-bold text-ink">{template.name}</h2>
        </div>
        {canWrite && tab === 'subjects' && (
          <Button onClick={() => setAdding(true)}>Add {template.subjectNoun.toLowerCase()}s</Button>
        )}
      </section>

      <div className="flex gap-1 border-b border-outline" role="tablist">
        {(
          [
            { id: 'subjects', label: `${template.subjectNoun}s` },
            { id: 'overview', label: 'Category details' },
            { id: 'pipeline', label: 'How guides get written' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-primary text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!readiness.ready && tab === 'subjects' && (
        <Banner variant="warning">
          <Banner.Title>This category can’t write anything yet</Banner.Title>
          <Banner.Description>
            It still needs {readiness.missing.join(' and ')}. Without them the assistant would write
            generic content that looks right and isn’t.{' '}
            <button
              type="button"
              className="text-link underline"
              onClick={() =>
                setTab(readiness.missing[0]?.includes('briefing') ? 'overview' : 'pipeline')
              }
            >
              Set it up
            </button>
          </Banner.Description>
        </Banner>
      )}

      {tab === 'overview' && <CategoryOverviewTab key={template.id} template={template} />}
      {tab === 'pipeline' && <CategoryPipelineTab key={template.id} template={template} />}

      <div className={tab === 'subjects' ? 'flex flex-wrap items-center gap-3' : 'hidden'}>
        <div className="w-72 shrink-0">
          <Input
            value={search}
            onChange={(e) => setParam('q', e.target.value, '')}
            placeholder={`Find a ${template.subjectNoun.toLowerCase()}…`}
            aria-label={`Find a ${template.subjectNoun.toLowerCase()}`}
            className="w-full"
          />
        </div>
        {kinds.length > 0 && (
          <div className="w-48 shrink-0">
            <Select
              value={[kindFilter]}
              onChange={(v) => setParam('type', v[0] ?? 'all', 'all')}
              options={[
                { value: 'all', label: 'All types' },
                ...kinds.map((k) => ({ value: k, label: k })),
              ]}
              aria-label="Filter by type"
              searchable={false}
            />
          </div>
        )}
        {(kindFilter !== 'all' || q.length > 0) && (
          <span className="text-sm text-ink-muted">
            {rows.length} of {all.length}
          </span>
        )}
      </div>

      <div
        className={
          tab === 'subjects'
            ? 'overflow-x-auto rounded-lg border border-outline bg-surface-elevated'
            : 'hidden'
        }
      >
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{template.subjectNoun}</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Aliases</Table.HeaderCell>
              <Table.HeaderCell>{template.itemNoun}s</Table.HeaderCell>
              <Table.HeaderCell>Docs</Table.HeaderCell>
              {canWrite && <Table.HeaderCell />}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={canWrite ? 6 : 5} className="text-ink-subtle">
                  {all.length > 0
                    ? 'Nothing matches those filters.'
                    : canWrite
                      ? `Nothing here yet — use “Add ${template.subjectNoun.toLowerCase()}s” to get started.`
                      : 'Nothing here yet.'}
                </Table.Cell>
              </Table.Row>
            )}
            {rows.map((s) => (
              <Table.Row
                key={s.id}
                className="cursor-pointer hover:bg-surface-hover"
                onClick={() => void navigate(`/knowledge/subjects/${s.id}/review`)}
              >
                <Table.Cell>
                  <span className="font-medium text-ink">{s.name}</span>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap text-ink-muted">
                  {s.kind ?? <span className="text-ink-subtle">—</span>}
                </Table.Cell>
                <Table.Cell>
                  {s.aliases.length === 0 ? (
                    <span className="text-ink-subtle">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {s.aliases.map((a) => (
                        <Badge key={a} variant="neutral">
                          {a}
                        </Badge>
                      ))}
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell className="text-ink-muted">{s.itemCount}</Table.Cell>
                <Table.Cell className="text-ink-muted">{s.documentCount}</Table.Cell>
                {canWrite && (
                  <Table.Cell className="whitespace-nowrap text-right">
                    <IconButton
                      aria-label={`Delete ${s.name}`}
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation(); // the row opens the subject
                        setDeleting(s);
                      }}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>

      {deleting && (
        <Modal open onOpenChange={(open) => !open && setDeleting(null)}>
          <Modal.Header>
            <Modal.Title>Delete {deleting.name}?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              This removes {deleting.itemCount} {template.itemNoun.toLowerCase()}
              {deleting.itemCount === 1 ? '' : 's'} and stops them being used in answers. It can’t
              be undone.
            </p>
            {deleting.documentCount > 0 && (
              <p className="mt-3 text-sm text-ink-muted">
                Its {deleting.documentCount} document
                {deleting.documentCount === 1 ? ' is' : 's are'} kept. Any tagged only to{' '}
                {deleting.name} stop being used until you tag them with other equipment.
              </p>
            )}
            {remove.error && (
              <p className="mt-3 text-sm text-danger">{(remove.error as Error).message}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate(deleting.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : `Delete ${template.subjectNoun.toLowerCase()}`}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {adding && (
        <NewSubjectModal template={template} knownKinds={kinds} onClose={() => setAdding(false)} />
      )}
    </div>
  );
};
